/**
 * Push/pull Chat OS local state ↔ FastAPI /api/prometheus/os-sync
 * (Supabase via prometheus_store, or JSON fallback). GLB stays in IndexedDB.
 */

import { fetchApi } from "@/lib/apiClient";
import { listInventory } from "@/lib/prometheusInventoryStore";
import { exportAllStudyPackages, mergeRemoteStudyPackages } from "@/lib/prometheusStudyStore";

const SYNC_META_KEY = "prometheus_os_sync_meta_v1";
const INVENTORY_KEY = "prometheus_inventory_v1";
const META_KEY = "prometheus_project_meta_v1";
const MODULE_QUIZ_KEY = "prometheus_university_module_quiz_v1";
const TRACK_QUIZ_KEY = "prometheus_university_quiz_v1";
const PLAN_KEY = "prometheus_active_plan_v1";
const MODE_KEY = "prometheus_session_mode_v1";
const PROJECTS_KEY = "prometheus_engineering_projects";
const LIFECYCLE_KEY = "prometheus_lifecycle_projects";
const DEVICES_KEY = "prometheus_device_registry_v1";

export type OsSyncBundle = {
  id: string;
  updatedAt: string | null;
  empty?: boolean;
  inventory: unknown[];
  projectMeta: Record<string, unknown>;
  moduleQuizzes: Record<string, boolean>;
  trackQuizzes: Record<string, boolean>;
  activePlan: unknown | null;
  sessionMode: string | null;
  engineeringProjects: unknown[];
  devices: unknown[];
  /** Mesa estudio packages keyed by projectId (merged on pull, not overwritten) */
  studyPackages?: Record<string, unknown>;
};

export type SyncMeta = {
  lastPushAt?: string;
  lastPullAt?: string;
  lastRemoteUpdatedAt?: string | null;
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getSyncMeta(): SyncMeta {
  return readJson<SyncMeta>(SYNC_META_KEY, {});
}

function setSyncMeta(patch: Partial<SyncMeta>) {
  const next = { ...getSyncMeta(), ...patch };
  writeJson(SYNC_META_KEY, next);
  window.dispatchEvent(new Event("prometheus-os-sync-changed"));
}

function inventoryForBundle(): unknown[] {
  try {
    return listInventory();
  } catch {
    return [];
  }
}

export function collectLocalOsBundle(): OsSyncBundle {
  const invSnap = readJson<{ items?: unknown[] } | unknown[]>(INVENTORY_KEY, []);
  const inventory = Array.isArray(invSnap)
    ? invSnap
    : Array.isArray(invSnap.items)
      ? invSnap.items
      : inventoryForBundle();

  return {
    id: "chat-os-v1",
    updatedAt: new Date().toISOString(),
    inventory,
    projectMeta: readJson(META_KEY, {}),
    moduleQuizzes: readJson(MODULE_QUIZ_KEY, {}),
    trackQuizzes: readJson(TRACK_QUIZ_KEY, {}),
    activePlan: readJson(PLAN_KEY, null),
    sessionMode: (() => {
      try {
        return localStorage.getItem(MODE_KEY);
      } catch {
        return null;
      }
    })(),
    engineeringProjects: readJson(PROJECTS_KEY, readJson(LIFECYCLE_KEY, [])),
    devices: readJson(DEVICES_KEY, []),
    studyPackages: exportAllStudyPackages(),
  };
}

export async function fetchRemoteOsBundle(): Promise<OsSyncBundle> {
  return fetchApi<OsSyncBundle>("/api/prometheus/os-sync", { method: "GET" }, 15000);
}

export async function pushOsBundle(): Promise<OsSyncBundle> {
  const bundle = collectLocalOsBundle();
  const saved = await fetchApi<OsSyncBundle>(
    "/api/prometheus/os-sync",
    { method: "PUT", body: JSON.stringify(bundle) },
    30000
  );
  setSyncMeta({
    lastPushAt: new Date().toISOString(),
    lastRemoteUpdatedAt: saved.updatedAt,
  });
  return saved;
}

export async function pullOsBundle(opts?: {
  /** If true, apply even when remote looks older */
  force?: boolean;
}): Promise<{ applied: boolean; remote: OsSyncBundle; reason?: string }> {
  const remote = await fetchRemoteOsBundle();
  if (remote.empty || !remote.updatedAt) {
    return { applied: false, remote, reason: "empty" };
  }

  const meta = getSyncMeta();
  if (
    !opts?.force &&
    meta.lastRemoteUpdatedAt &&
    remote.updatedAt &&
    remote.updatedAt <= meta.lastRemoteUpdatedAt &&
    meta.lastPullAt
  ) {
    return { applied: false, remote, reason: "already-fresh" };
  }

  applyRemoteBundle(remote);
  setSyncMeta({
    lastPullAt: new Date().toISOString(),
    lastRemoteUpdatedAt: remote.updatedAt,
  });
  return { applied: true, remote };
}

function applyRemoteBundle(remote: OsSyncBundle) {
  if (Array.isArray(remote.inventory)) {
    writeJson(INVENTORY_KEY, {
      items: remote.inventory,
      updatedAt: remote.updatedAt || new Date().toISOString(),
    });
    // Legacy tools mirror
    try {
      const names = (remote.inventory as Array<{ name?: string; kind?: string }>)
        .filter((i) => i && (i.kind === "tool" || i.kind === "other" || !i.kind))
        .map((i) => i.name)
        .filter(Boolean);
      localStorage.setItem("PROMETHEUS_OWNED_TOOLS", JSON.stringify(names));
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event("prometheus-inventory-changed"));
  }

  if (remote.projectMeta && typeof remote.projectMeta === "object") {
    writeJson(META_KEY, remote.projectMeta);
    window.dispatchEvent(new Event("prometheus-project-meta-changed"));
  }

  if (remote.moduleQuizzes && typeof remote.moduleQuizzes === "object") {
    writeJson(MODULE_QUIZ_KEY, remote.moduleQuizzes);
    window.dispatchEvent(new Event("prometheus-university-changed"));
  }
  if (remote.trackQuizzes && typeof remote.trackQuizzes === "object") {
    writeJson(TRACK_QUIZ_KEY, remote.trackQuizzes);
  }

  if (remote.activePlan === null) {
    localStorage.removeItem(PLAN_KEY);
  } else if (remote.activePlan) {
    writeJson(PLAN_KEY, remote.activePlan);
  }
  window.dispatchEvent(new Event("prometheus-plan-changed"));

  if (remote.sessionMode === "chat" || remote.sessionMode === "plan" || remote.sessionMode === "ask") {
    localStorage.setItem(MODE_KEY, remote.sessionMode);
    window.dispatchEvent(
      new CustomEvent("prometheus-session-mode", { detail: { mode: remote.sessionMode } })
    );
  }

  if (Array.isArray(remote.engineeringProjects) && remote.engineeringProjects.length > 0) {
    const json = JSON.stringify(remote.engineeringProjects);
    localStorage.setItem(PROJECTS_KEY, json);
    localStorage.setItem(LIFECYCLE_KEY, json);
  }

  if (Array.isArray(remote.devices)) {
    writeJson(DEVICES_KEY, remote.devices);
  }

  // Older backends omit the key → nothing to merge; local studies stay untouched.
  if (remote.studyPackages) mergeRemoteStudyPackages(remote.studyPackages);
}
